import { useApiTestingLanguage } from './useApiTestingLanguage';
/**
 * ExecutionHistoryCard Component
 * 
 * Displays a single test execution history entry with status, timing, and error details.
 * Extracted from APITesting.js for better maintainability.
 */

import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Eye,
  Globe,
  Target,
  XCircle
} from 'lucide-react';

// Helper function to format execution time with timezone
const formatExecutionTime = (timeString, language) => {
  const executedTime = new Date(timeString + (timeString.includes('Z') ? '' : 'Z'));
  const options = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  return executedTime.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', options);
};

// Helper function to get status color
const getStatusColorClass = (status) => {
  if (status === 'PASS') return 'bg-green-500';
  if (status === 'FAIL') return 'bg-red-500';
  return 'bg-yellow-500';
};

const getStatusTextClass = (status) => {
  if (status === 'PASS') return 'text-green-600';
  if (status === 'FAIL') return 'text-red-600';
  return 'text-yellow-600';
};

export const ExecutionHistoryCard = ({
  execution,
  test,
  onViewDetails
}) => {
  const { t, language } = useApiTestingLanguage();
  const isFailed = execution.status === 'FAIL' || execution.status === 'failed';
  
  return (
    <Card className="p-4" data-testid={`execution-card-${execution.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${getStatusColorClass(execution.status)}`} />
          <div>
            <div className="mb-1">
              <span className="font-semibold text-gray-800">{test.name}</span>
              <div className="text-xs text-gray-500 mt-1">
                {test.method} {test.url}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium">{t("Execution #")}{execution.execution_number}</span>
              <span className="text-gray-400">•</span>
              <span className="text-sm text-gray-600">
                {formatExecutionTime(execution.started_at, language)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className={`text-sm font-medium ${getStatusTextClass(execution.status)}`}>
              {t(execution.status)}
            </div>
            {execution.response_status_code && (
              <div className="text-xs text-gray-500">
                {t("Status:")} {execution.response_status_code}
              </div>
            )}
            {execution.duration_ms && (
              <div className="text-xs text-gray-500">
                {execution.duration_ms}ms
              </div>
            )}
          </div>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => onViewDetails(execution)}
            data-testid={`btn-view-details-${execution.id}`}
          >
            <Eye className="h-4 w-4 mr-1" />
            {t("Details")}
          </Button>
        </div>
      </div>
      
      {/* Error Summary for Failed Executions */}
      {isFailed && (
        <div className="mt-3 p-3 bg-red-50 rounded border-l-4 border-red-400">
          {execution.error_message ? (
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-700 font-medium">{t("Network Error:")}</span>
              <span className="text-sm text-red-700">
                {execution.error_message.substring(0, 100)}
                {execution.error_message.length > 100 ? '...' : ''}
              </span>
            </div>
          ) : execution.response_status_code && execution.response_status_code >= 400 ? (
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-700 font-medium">HTTP {execution.response_status_code} {t("Error")}</span>
              {execution.response_body && execution.response_body.length < 100 && (
                <span className="text-sm text-red-600">: {execution.response_body}</span>
              )}
            </div>
          ) : execution.assertion_results && execution.assertion_results.some(r => !r.passed) ? (
            <div className="flex items-center space-x-2">
              <Target className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-700 font-medium">
                {t(execution.assertion_results.filter(r => !r.passed).length === 1 ? '{v0} assertion failed' : '{v0} assertions failed', { v0: execution.assertion_results.filter(r => !r.passed).length })}
              </span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-700">{t("Test failed - click Details for more info")}</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
